import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { $TSFixMe } from 'types/ts-migrate'
import Button from 'views/components/atoms/Button/Button'
import Modal from 'views/components/molecules/Modal/Modal'

/**
 * RouterPromptModal - Prompts user to save or discard changes before navigating away
 *
 * This component intercepts user-initiated navigation attempts (e.g., clicking browser back,
 * clicking a link) when the form has unsaved changes. It shows a custom modal asking the
 * user whether to save or discard changes.
 *
 * IMPORTANT: This component does NOT block programmatic navigation (e.g., via Redux epics)
 * when isSubmitting is true, allowing the form to redirect after successful submission.
 */
type Props = {
  /** Whether to block navigation (typically form's dirty state) */
  when: boolean
  /** Whether the form is valid (determines if Save button is shown) */
  isValid?: boolean
  /** Whether form is currently submitting (disables blocking during submission) */
  isSubmitting?: boolean
  /**
   * Pathnames that navigate without prompting, for deliberate shortcuts out of the form
   * (e.g. "Add now" on a beneficiary with no bank details). Unsaved changes are discarded.
   */
  allowPathname?: (pathname: string) => boolean
  /** Callback when user clicks Save */
  onSave: () => void
  /** Callback when user clicks Discard */
  onDiscard: () => void
}

const RouterPromptModal: FC<Props> = props => {
  const { when, isValid = true, isSubmitting = false, allowPathname, onSave, onDiscard } = props

  const history = useHistory()
  // Store the unblock function to properly cleanup across effect re-runs (important for React 18 StrictMode)
  const unblockRef = useRef<(() => void) | null>(null)
  // Held in a ref so an inline predicate doesn't re-register the blocker on every render
  const allowPathnameRef = useRef(allowPathname)

  useEffect(() => {
    allowPathnameRef.current = allowPathname
  }, [allowPathname])

  const [showPrompt, setShowPrompt] = useState(false)
  const [currentPath, setCurrentPath] = useState('')

  useEffect(() => {
    // Clean up any existing blocker before setting a new one
    if (unblockRef.current) {
      unblockRef.current()
      unblockRef.current = null
    }

    // Only block user-initiated navigation when:
    // 1. when = true (form has unsaved changes)
    // 2. isSubmitting = false (not currently submitting)
    // This allows programmatic navigation from Redux epics after form submission
    if (when && !isSubmitting) {
      unblockRef.current = history.block(prompt => {
        // Let allowed destinations through untouched — returning nothing leaves history
        // to continue with the navigation
        if (allowPathnameRef.current?.(prompt.pathname)) return

        setCurrentPath(prompt.pathname)
        setShowPrompt(true)
        // Return false to block the navigation and show our custom modal
        // (returning a string would show browser's native confirm dialog instead)
        return false
      })
    }

    // Cleanup function to remove the blocker when component unmounts or dependencies change
    return () => {
      if (unblockRef.current) {
        unblockRef.current()
        unblockRef.current = null
      }
    }
  }, [history, when, isSubmitting])

  const handleSave = useCallback(async () => {
    if (onSave) {
      /**
       * Only a save that reports back that it's safe to leave resumes the blocked navigation
       * here. Formik's `submitForm` resolves undefined, so most callers fall through and
       * redirect from their own submit handler instead — either way the prompt has done its
       * job and must come down, or it sits over the page with no way out but Discard.
       */
      const canRoute: $TSFixMe = await Promise.resolve(onSave())
      if (canRoute) {
        if (unblockRef.current) {
          unblockRef.current()
          unblockRef.current = null
        }
        history.push(currentPath)
      }
    }
    setShowPrompt(false)
  }, [currentPath, history, onSave])

  const handleDiscard = useCallback(async () => {
    if (onDiscard) {
      const canRoute: $TSFixMe = await Promise.resolve(onDiscard())
      if (canRoute) {
        if (unblockRef.current) {
          unblockRef.current()
          unblockRef.current = null
        }
        history.push(currentPath)
      }
    }
    setShowPrompt(false)
  }, [currentPath, history, onDiscard])

  return showPrompt ? (
    <Modal showClose={true} isOpen={showPrompt} onClose={() => setShowPrompt(false)}>
      <h3 className="Modal_Title">Unsaved changes</h3>
      <div className="Modal_Content">
        <p>Before you go, would you like to save or discard the changes you made?</p>
      </div>
      <div className="Modal_Actions">
        {isValid ? (
          <Button link onClick={handleSave}>
            Save
          </Button>
        ) : (
          <Button link onClick={() => setShowPrompt(false)}>
            Cancel
          </Button>
        )}
        <Button link onClick={handleDiscard}>
          Discard
        </Button>
      </div>
    </Modal>
  ) : null
}

export default RouterPromptModal
